import { GameState, Zone } from './types';
import { generateScenario } from './scenarios';
import { Renderer } from './renderer';

const BEST_SCORE_KEY = 'reflex-tennis-best';

export class Game {
  private canvas: HTMLCanvasElement;
  private renderer: Renderer;
  private state: GameState;
  private animFrame: number = 0;
  private boundTap: (e: TouchEvent | MouseEvent) => void;

  constructor(private container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    container.innerHTML = '';
    container.appendChild(this.canvas);

    this.state = {
      phase: 'menu',
      score: 0,
      bestScore: parseInt(localStorage.getItem(BEST_SCORE_KEY) || '0', 10),
      streak: 0,
      currentScenario: null,
      scenarioStartTime: 0,
      lastReactionTime: 0,
      roundBonus: 0,
      selectedZone: null,
    };

    this.renderer = new Renderer(this.canvas);
    this.resize();
    window.addEventListener('resize', () => this.resize());

    this.boundTap = (e: TouchEvent | MouseEvent) => this.onTap(e);
    this.canvas.addEventListener('touchstart', this.boundTap, { passive: false });
    this.canvas.addEventListener('mousedown', this.boundTap);

    this.loop();
  }

  private resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.renderer.resize(w * dpr, h * dpr);
  }

  private loop() {
    this.animFrame = requestAnimationFrame(() => this.loop());

    let timerPct = 1;
    if (this.state.phase === 'playing' && this.state.currentScenario) {
      const elapsed = Date.now() - this.state.scenarioStartTime;
      timerPct = Math.max(0, 1 - elapsed / this.state.currentScenario.timeLimit);

      if (timerPct <= 0) {
        this.state.phase = 'timeout';
        this.saveBest();
      }
    }

    this.renderer.render(this.state, timerPct);
  }

  private onTap(e: TouchEvent | MouseEvent) {
    e.preventDefault();
    const pageX = 'touches' in e ? e.touches[0].pageX : e.pageX;
    const pageY = 'touches' in e ? e.touches[0].pageY : e.pageY;

    if (this.state.phase === 'menu') {
      this.startGame();
      return;
    }

    if (this.state.phase === 'wrong' || this.state.phase === 'timeout') {
      this.state.phase = 'menu';
      return;
    }

    if (this.state.phase !== 'playing' || !this.state.currentScenario) return;

    const { x, y } = this.renderer.pageToCanvas(pageX, pageY);
    const zone = this.renderer.getZoneFromPoint(x, y);
    if (!zone) return;

    this.handleAnswer(zone);
  }

  private startGame() {
    this.state.score = 0;
    this.state.streak = 0;
    this.state.roundBonus = 0;
    this.state.selectedZone = null;
    this.nextScenario();
  }

  private nextScenario() {
    this.state.phase = 'playing';
    this.state.currentScenario = generateScenario(this.state.streak);
    this.state.scenarioStartTime = Date.now();
    this.state.selectedZone = null;
    this.state.roundBonus = 0;
  }

  private handleAnswer(zone: Zone) {
    const scenario = this.state.currentScenario!;
    const reactionTime = Date.now() - this.state.scenarioStartTime;
    this.state.lastReactionTime = reactionTime;
    this.state.selectedZone = zone;

    if (zone === scenario.correctReturn) {
      // Correct! Score based on speed
      this.state.streak++;
      const timeRatio = 1 - reactionTime / scenario.timeLimit;
      const speedBonus = Math.floor(timeRatio * 100);
      const streakBonus = this.state.streak * 5;
      this.state.roundBonus = 10 + speedBonus + streakBonus;
      this.state.score += this.state.roundBonus;

      // Brief flash then next scenario
      setTimeout(() => {
        if (this.state.phase === 'playing') {
          this.nextScenario();
        }
      }, 300);
    } else {
      // Wrong — game over
      this.state.phase = 'wrong';
      this.saveBest();
    }
  }

  private saveBest() {
    if (this.state.score > this.state.bestScore) {
      this.state.bestScore = this.state.score;
      localStorage.setItem(BEST_SCORE_KEY, String(this.state.bestScore));
    }
  }

  destroy() {
    cancelAnimationFrame(this.animFrame);
    window.removeEventListener('resize', () => this.resize());
    this.canvas.removeEventListener('touchstart', this.boundTap);
    this.canvas.removeEventListener('mousedown', this.boundTap);
  }
}
